import { Grow, IconButton, Tooltip } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import AddShoppingCartIcon from '@material-ui/icons/AddShoppingCart';
import RemoveShoppingCartIcon from '@material-ui/icons/RemoveShoppingCart';
import SearchIcon from '@material-ui/icons/Search';
import CopyButton from 'components/CopyButton';
import MolCard from 'components/MolCard';
import { SearchResultsGroupMember } from 'features/searchMolecules/searchMoleculesSlice';
import { addItemToCart, CartMember, removeFromCart } from 'features/shoppingCart/shoppingCartSlice';
import { useThunkDispatch } from 'hooks';
import React from 'react';
import { useTranslation } from 'react-i18next';

import CalculationsTable from '../../../components/CalculationsTable';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    actionsRoot: {
      display: 'block',
      position: 'absolute',
      top: -theme.spacing(1),
      left: 0,
      right: 0,
    },
  }),
);

interface Props {
  groupMember: SearchResultsGroupMember;
  loadAsQuery: (smiles: string) => void;
  refmol: string;
  calcs: { [key: string]: string };
  debug?: boolean;
  cartItems: CartMember[];
}

export default ({ groupMember, refmol, calcs, cartItems, loadAsQuery }: Readonly<Props>) => {
  const { t } = useTranslation(['common', 'results']);
  const classes = useStyles();

  const dispatch = useThunkDispatch();

  const isInCart = cartItems.map((item) => item.id).includes(groupMember.id);
  const handleButtonOnClick = () => {
    if (isInCart) {
      dispatch(removeFromCart(groupMember.id));
    } else {
      dispatch(addItemToCart({ ...groupMember, isLoading: false, selectedItems: [] }));
    }
  };

  return (
    <MolCard
      bgColor={isInCart ? '#f5f5f5' : '#fcfcfc'}
      depictmcs={refmol}
      depictNoStereo={false}
      smiles={groupMember.smiles}
      raised={isInCart}
      actionsProps={{ className: classes.actionsRoot }}
      actions={(hover?: boolean) => (
        <Grow in={hover}>
          <div>
            <Tooltip arrow title={t(isInCart ? 'remove_from_cart' : 'results:add_to_cart')!}>
              <IconButton onClick={handleButtonOnClick}>
                {isInCart ? <RemoveShoppingCartIcon /> : <AddShoppingCartIcon />}
              </IconButton>
            </Tooltip>
            <CopyButton dump={groupMember.smiles} tooltip={t('results:copy_smiles')!} />
            <Tooltip arrow title={t('results:set_query_mol')!}>
              <IconButton onClick={() => loadAsQuery(groupMember.smiles)}>
                <SearchIcon />
              </IconButton>
            </Tooltip>
          </div>
        </Grow>
      )}
    >
      <CalculationsTable groupMember={groupMember} calcs={calcs} />
    </MolCard>
  );
};
